import type { CSSProperties } from 'react';

/**
 * WORKSTATION PANE GEOMETRY — one token, both panes.
 *
 * The left explorer and the right chat pane must be the SAME width (owner 2026-08-16: "both left pane and
 * right pane should be same width, and prefer to be narrower"). They used to be declared in four unrelated
 * places — `md:w-72` on the left pane, `md:left-[18rem]` on the Studio's left reservation, and a separate
 * 380px constant for the right — so they drifted apart by 92px. Every one of those now reads this token, so
 * a change here moves all four together and they cannot disagree again.
 *
 * 17rem / 272px is the narrow end the owner asked for, measured against the real content rather than picked:
 * an episode row nested under a series (the deepest thing the explorer draws) keeps 212px for its title —
 * enough for ~30 characters at 13px, which covers every production title in the catalogue — and a contact
 * row keeps 168px beside its 32px group avatar. Below ~16rem the nested episode titles start truncating
 * mid-word; above 18rem the centre canvas pays for room neither pane uses.
 */
export const SIDE_PANE_WIDTH = '17rem';

/**
 * What a FOLDED pane leaves behind — a SLIM NAMED STRIP, not an empty gutter (owner 2026-08-24: collapsed
 * "not showing anything", with indexalpha.ai's "SEARCH HISTORY" rail as the reference). 2rem / `w-8` is wide
 * enough for the pane's name set vertically and a round expand chevron, and narrow enough that a folded pane
 * still visibly gives the canvas its space back. Kept identical on both sides.
 */
export const SIDE_PANE_RAIL = '2rem';

/** The far-left activity bar — always present, never folds. Tailwind's `w-12`. */
export const ACTIVITY_BAR_WIDTH = '3rem';

/**
 * DRAG BOUNDS (owner 2026-08-21: "allow user to also drag the border to expand/shrink like the panes in
 * vscode"). A dragged pane is still a pane: below 14rem the explorer's nested episode titles start losing
 * words (the measurement behind the default applies to the floor too), and above 28rem the centre canvas —
 * the thing both panes exist to serve — is starved. A drag that hits either end simply stops there.
 */
export const SIDE_PANE_MIN_WIDTH = '14rem';
export const SIDE_PANE_MAX_WIDTH = '28rem';

/**
 * Publish a width to CSS so Tailwind arbitrary values (`w-[var(--wt-pane)]`) can consume it — a class name
 * cannot be built from a JS string at runtime (the JIT only sees literals in the source), and a dragged pane
 * changes its width on every pointer move. Defaults to the shared token.
 */
export const paneVars = (width: string = SIDE_PANE_WIDTH): CSSProperties =>
  ({ ['--wt-pane' as string]: width } as CSSProperties);

/** Convert one of the rem tokens above to px against the document's real root font size. */
export const remToPx = (value: string): number => {
  const n = parseFloat(value);
  if (value.trim().endsWith('rem')) {
    const root = typeof window !== 'undefined'
      ? parseFloat(getComputedStyle(document.documentElement).fontSize)
      : 16;
    return n * (root || 16);
  }
  return n;
};
